import { z } from 'zod';

import { baseToolDefinitionSchema } from './mcpIntegration';

export const httpMethodSchema = z.enum([
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
]);

export type HttpMethod = z.infer<typeof httpMethodSchema>;

/**
 * Where a parameter is placed on the outgoing request.
 * - `path`  → substituted into a `{name}` placeholder in the URL.
 * - `query` → appended to the query string.
 *
 * The request *body* is modeled separately (see `bodyExample`/`bodyInstructions`)
 * as an annotated JSON template the model generates wholesale — bodies are
 * nested trees, not flat key/value pairs. Static request *headers* are
 * admin-configured (often secret) and live in the encrypted `headers` field.
 */
export const httpToolParamLocationSchema = z.enum(['path', 'query']);

export type HttpToolParamLocation = z.infer<typeof httpToolParamLocationSchema>;

export const httpToolParamTypeSchema = z.enum([
  'string',
  'number',
  'integer',
  'boolean',
]);

export type HttpToolParamType = z.infer<typeof httpToolParamTypeSchema>;

/**
 * How a parameter's value is supplied:
 * - `llm`    → the model extracts it at call time (part of the input schema);
 *              `description` guides extraction, `enumValues` constrains it.
 * - `static` → a fixed admin-configured `value`, never exposed to the model.
 */
export const httpToolParamValueTypeSchema = z.enum(['llm', 'static']);

export type HttpToolParamValueType = z.infer<
  typeof httpToolParamValueTypeSchema
>;

/**
 * A single path/query parameter. `llm` parameters compile into the tool's input
 * JSON Schema (raw passthrough, D2) and are filled by the model; `static`
 * parameters carry a fixed value applied server-side, never shown to the model.
 */
export const httpToolParamSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .max(60)
      .regex(
        /^[A-Za-z]\w*$/,
        'Must start with a letter; letters, digits and underscores only',
      ),
    location: httpToolParamLocationSchema,
    type: httpToolParamTypeSchema.default('string'),
    valueType: httpToolParamValueTypeSchema.default('llm'),
    description: z.string().max(300).optional(),
    required: z.boolean().default(false),
    /** Fixed value, used when `valueType === 'static'`. */
    value: z.string().optional(),
    /**
     * Optional closed set of values the model may choose from
     * (`valueType === 'llm'`). Empty/omitted means any value of `type`.
     */
    enumValues: z.array(z.string()).optional(),
  })
  .superRefine((param, ctx) => {
    if (
      param.valueType === 'static' &&
      (param.value === undefined || param.value === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'A static parameter requires a value',
      });
    }
  });

export type HttpToolParam = z.infer<typeof httpToolParamSchema>;

/**
 * Shared HTTP-tool shape, built on the common tool-definition base (D4). The
 * `name` is tightened to an LLM-friendly identifier and `description` is
 * required (the model relies on it to decide when to call the tool).
 */
const httpToolBaseSchema = baseToolDefinitionSchema.extend({
  name: z
    .string()
    .min(1)
    .max(60)
    .regex(
      /^[a-z][\d_a-z]*$/,
      'Lowercase letters, digits and underscores; must start with a letter',
    ),
  description: z.string().min(10).max(500),
  url: z.string().url(),
  method: httpMethodSchema,
  /** Path and query parameters (flat key/value, static-or-LLM). */
  parameters: z.array(httpToolParamSchema).default([]),
  /**
   * Annotated JSON example of the request body (jsonc — comments allowed). The
   * model is shown this verbatim and generates the full body to match; comments
   * tell it which values are static and how to fill the rest. Stored raw and
   * never parsed server-side.
   */
  bodyExample: z.string().max(8000).optional(),
  /** Prose guidance for generating the request body. */
  bodyInstructions: z.string().max(2000).optional(),
  /** Optional JSONPath-ish/template extraction applied to the JSON response. */
  responseMapping: z.string().max(300).optional(),
  timeoutMs: z.number().int().min(1000).max(30000).default(10000),
});

/**
 * Create/update input. `headers` carries static request headers (auth tokens,
 * API keys) — encrypted at rest, never returned raw.
 */
export const createHttpToolSchema = httpToolBaseSchema.extend({
  headers: z.record(z.string()).optional(),
});

export const updateHttpToolSchema = createHttpToolSchema
  .partial()
  .extend({
    id: z.string().uuid(),
    /** Omitted keys are preserved; null removes a stored header. */
    headerPatch: z
      .record(z.string().regex(/^[\w!#$%&'*+.^`|~-]+$/), z.string().nullable())
      .optional(),
  })
  .refine(
    input => input.headers === undefined || input.headerPatch === undefined,
    {
      message: 'Use either headers replacement or headerPatch, not both',
      path: ['headerPatch'],
    },
  );

/**
 * API output. Header *values* are never serialized — only the configured header
 * names are exposed so the admin UI can show which headers exist without
 * leaking secrets (mirrors the MCP `hasApiKey` masking).
 */
export const httpToolOutputSchema = httpToolBaseSchema.extend({
  id: z.string().uuid(),
  headerKeys: z.array(z.string()).optional(),
  lastTestedAt: z.date().optional(),
});

export type CreateHttpToolDTO = z.infer<typeof createHttpToolSchema>;
export type UpdateHttpToolDTO = z.infer<typeof updateHttpToolSchema>;
export type HttpToolOutput = z.infer<typeof httpToolOutputSchema>;
